import {
  APIEntityType,
  ColorFormatting,
  FieldFormattingVersion,
  isListFieldFormatting,
  ListFieldFormatting,
} from '@ardoq/api-types';
import type {
  ComponentOrReferenceFormattingRule,
  FormattingRule,
} from '@ardoq/data-model';
import { getOperatorTranslations } from './operatorMappings';
import {
  transformValue,
  type SelectOption,
  transformTypeName,
} from './valueTransformations';
import { QueryBuilderFilterType } from '@ardoq/query-builder';
import {
  canSquashListField,
  isComponentOrReferenceFormattingRule,
} from '../../../utils';
import { v4 as uuidv4 } from 'uuid';
import { isNil, isString } from 'lodash';
import { needsRedact } from './redact';

type MigrationResult = {
  migratedRule: FormattingRule;
  warnings: string[];
  errors: string[];
  success: boolean;
  skip: boolean;
};

type MigrationMetadata = {
  getFieldType: (
    field: string,
    entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE
  ) => QueryBuilderFilterType | undefined;
  getSelectOptions: (
    field: string,
    entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE
  ) => SelectOption[] | undefined;
};

/**
 * Translates a single formatting rule from old (version 0) to new (version 1).
 */
const translateFormattingRule = (
  oldRule: FormattingRule,
  metadata: MigrationMetadata
): MigrationResult[] => {
  if (oldRule.version === FieldFormattingVersion.NEW_CONDITIONAL_FORMATTING) {
    return [
      {
        migratedRule: oldRule,
        warnings: [],
        errors: [],
        success: true,
        skip: true,
      },
    ];
  }

  // valid TAG rules just need version update
  // old TAG rules, don't have comparators. Transforming to is/is not is handled outside this migrations
  if (oldRule.type === APIEntityType.TAG) {
    if (!isNil(oldRule.value)) {
      return [
        {
          migratedRule: {
            ...oldRule,
            version: FieldFormattingVersion.NEW_CONDITIONAL_FORMATTING,
          },
          warnings: [],
          errors: [],
          success: true,
          skip: false,
        },
      ];
    }

    return [
      {
        migratedRule: {
          ...oldRule,
        },
        warnings: [],
        errors: ['Rule TAG with Nil value'],
        success: false,
        skip: false,
      },
    ];
  }

  // Type assertion for component/reference rules
  const rule = oldRule as ComponentOrReferenceFormattingRule;

  const { field, comparator, isInverted, type } = rule;

  if (!field) {
    return [
      {
        migratedRule: rule,
        warnings: [],
        errors: ['Rule has no field defined'],
        success: false,
        skip: false,
      },
    ];
  }

  // Get filter type for the field
  const filterType = metadata.getFieldType(field, type);

  if (!filterType) {
    return [
      {
        migratedRule: rule,
        warnings: [],
        errors: [
          `Rule has no field type defined (field: "${needsRedact(field)}", type: "${type}")`,
        ],
        success: false,
        skip: false,
      },
    ];
  }

  // Get operator translation
  const translations = getOperatorTranslations(
    {
      oldComparator: comparator,
      filterType,
      isInverted,
      isEmptyValue: isNil(rule.value),
    },
    rule
  );

  if (!translations) {
    const valueMeta = [
      `valueType=${rule.value === null ? 'null' : Array.isArray(rule.value) ? 'array' : typeof rule.value}`,
      `isNil=${isNil(rule.value)}`,
      `isEmptyString=${isString(rule.value) && rule.value.trim() === ''}`,
    ].join(', ');

    // Fallback: keep same operator
    return [
      {
        migratedRule: {
          ...rule,
        },
        warnings: ['No translation'],
        errors: [
          `No translation found for ${comparator} (inverted: ${isInverted}) on ${filterType}; ${valueMeta}`,
        ],
        success: false,
        skip: false,
      },
    ];
  }

  return translations.map((translation, index) => {
    const errors: string[] = [];
    const warnings: string[] = [];
    // Add warning if translation has one
    if (translation.warning) {
      warnings.push(translation.warning);
    }

    // Get select options if needed for value transformation
    const selectOptions = metadata.getSelectOptions(field, type);

    // Transform value
    const valueTransformResult = transformValue(
      rule,
      translation.newComparator,
      selectOptions,
      translation.handleValueTransformation
    );
    warnings.push(...valueTransformResult.warnings);

    // Transform typeNames

    const typeNameTransformResult = transformTypeName(
      oldRule,
      selectOptions,
      translation.handleTypeNameTransformation
    );

    warnings.push(...typeNameTransformResult.warnings);

    // Build migrated rule
    const migratedRule: ComponentOrReferenceFormattingRule = {
      ...rule,
      __id: index > 0 ? uuidv4() : rule.__id, // in case if old rules translates in several new rules
      comparator: translation.newComparator,
      value: valueTransformResult.newValue,
      typeNames: typeNameTransformResult.newTypeNames,
      isInverted: false, // Always false in new system
      version: FieldFormattingVersion.NEW_CONDITIONAL_FORMATTING,
    };

    return {
      migratedRule,
      warnings,
      errors,
      success: errors.length === 0,
      skip: false,
    };
  });
};

/**
 * Batch migration result with summary statistics.
 */
type BatchMigrationResult = {
  migratedRules: FormattingRule[];
  originalRules: FormattingRule[];
  summary: {
    total: number;
    skip: number;
    successful: number;
    failed: number;
    withWarnings: number;
  };
  ruleResults: Map<string, MigrationResult[]>;
};

/**
 * Translates multiple formatting rules in batch.
 */
export const translateFormattingRules = (
  rules: FormattingRule[],
  metadata: MigrationMetadata,
  options: {
    stopOnError?: boolean;
  } = {}
): BatchMigrationResult => {
  // Keyed by original rule.__id. One input rule can expand into multiple MigrationResult entries.
  const ruleResults = new Map<string, MigrationResult[]>();
  const migratedRules: FormattingRule[] = [];
  let successful = 0;
  let failed = 0;
  let skip = 0;
  let withWarnings = 0;
  let isSquashingListField = false;

  for (const rule of rules) {
    const translatedFormattingRules = translateFormattingRule(rule, metadata);

    for (const result of translatedFormattingRules) {
      const existingResults = ruleResults.get(rule.__id);
      if (existingResults) {
        existingResults.push(result);
      } else {
        ruleResults.set(rule.__id, [result]);
      }

      const migratedRule = result.migratedRule;

      /**
       * List-field squashing:
       * Some source rules are represented as multiple consecutive formatting rules after translation
       * (e.g., a list-field rule followed by another list-field rule for the same target).
       *
       * In that case we "squash" (merge) them into a single rule:
       * - We pop the previously migrated rule (expected to be the matching list-field rule).
       * - We keep ONLY the previous rule's entries that have a non-empty `color` value.
       *   (This prevents overwriting newer/translated values with empty/irrelevant defaults.)
       * - We then merge those retained colored entries into the current rule's value and push back
       *   one combined rule.
       *
       * `isSquashingListField` tracks whether the immediately previous processed rule was a list-field
       * formatting rule eligible for squashing.
       */
      if (
        isComponentOrReferenceFormattingRule(migratedRule) &&
        !result.skip &&
        isListFieldFormatting(migratedRule.value)
      ) {
        if (isSquashingListField) {
          const prevCandidate = migratedRules[migratedRules.length - 1];

          // List-field squashing guard:
          //
          // We only want to squash (merge) list-field rules when they target the same field using the same comparator.
          // In case if they target different fields - we add it to final result as it and move to next rule
          if (
            !prevCandidate ||
            !isComponentOrReferenceFormattingRule(prevCandidate) ||
            !isListFieldFormatting(prevCandidate.value) ||
            !canSquashListField(prevCandidate, migratedRule)
          ) {
            isSquashingListField = true;
            migratedRules.push(migratedRule);
            continue;
          }

          // At this point we know `prevCandidate` is compatible, so it is safe to pop and merge it.
          const prevRule = migratedRules.pop();

          /**
           * Re-narrow the popped rule for TypeScript.
           * Runtime-wise this should already be safe because `prevCandidate` was validated
           * before `pop()`, but the compiler widens the popped value back to `FormattingRule`.
           */
          if (
            !prevRule ||
            !isComponentOrReferenceFormattingRule(prevRule) ||
            !isListFieldFormatting(prevRule.value)
          ) {
            if (prevRule) migratedRules.push(prevRule);
            migratedRules.push(migratedRule);
            continue;
          }

          const entries = Object.entries(prevRule.value) as Array<
            [string, ColorFormatting]
          >;

          const onlyValuesWithColor = Object.fromEntries(
            entries.filter(([_, v]) => (v.color ?? '').trim() !== '')
          );

          const migratedValue = migratedRule.value as ListFieldFormatting;

          migratedRules.push({
            ...migratedRule,
            value: { ...migratedValue, ...onlyValuesWithColor },
          });

          continue;
        } else {
          isSquashingListField = true;
        }
      } else {
        isSquashingListField = false;
      }

      if (result.success) {
        successful++;
        if (result.warnings.length > 0) {
          withWarnings++;
        }
        if (result.skip) {
          skip++;
        }

        migratedRules.push(result.migratedRule);
      } else {
        failed++;
        if (options.stopOnError) {
          break;
        }
        // On failure, include original rule
        migratedRules.push(rule);
      }
    }
  }

  return {
    migratedRules,
    originalRules: rules,
    summary: {
      total: rules.length,
      successful,
      skip,
      failed,
      withWarnings,
    },
    ruleResults,
  };
};
